'use client'

import { useAuthorization } from '@entities/authentication'
import { paymentEntityMutations } from '@entities/payment'
import {
  SubscribeDiscountBadge,
  SubscribeOriginalPrice,
  useSubscribeErrorStaticModel,
  useSubscribeTicketDestination,
} from '@entities/subscribes'
import { userEntityQueries } from '@entities/users'
import { BaseResponseSchema, CurriculumType, LangType, SubscribeTicket } from '@server/schemas'
import { useI18n } from '@shared/config/i18n'
import { UI_IDS } from '@shared/constants'
import { openAlertDialog, openConfirmDialogV2 } from '@shared/ui'
import { formatKrwAmount } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useRouter, useSearchParams } from 'next/navigation'
import { ComponentProps, ReactNode } from 'react'

import { BadgeV1, Button, Flex, Icon, Typography, VStack } from '@podo-app/design-system-temp'

type Props = {
  month: number
  isBest: boolean
  price: number
  catalogTicket?: SubscribeTicket
  descriptions: ReactNode[]
  subscribeTicketId: string
  curriculumType?: CurriculumType
  langType?: LangType
}

const SubscribeTicketCard = ({
  month,
  isBest,
  price,
  catalogTicket,
  descriptions,
  subscribeTicketId: subscribeId,
  curriculumType,
  langType,
}: Props) => {
  const productId = catalogTicket?.productId ?? undefined
  const displayPrice = productId ? catalogTicket?.finalPrice : price
  if (displayPrice === null || displayPrice === undefined) {
    throw new Error('Invalid KR product catalog pricing')
  }
  const { effectiveLocale, t } = useI18n()
  const { SUBSCRIBE_ERROR_MAP } = useSubscribeErrorStaticModel()
  const { isAuthorized, bearerToken } = useAuthorization()
  const { data: user } = useQuery({
    ...userEntityQueries.getCurrentUser({ bearerToken }),
    enabled: isAuthorized,
  })

  const router = useRouter()
  const search = useSearchParams()
  const { loginDestination } = useSubscribeTicketDestination(curriculumType)

  const validatePayment = useMutation({
    ...paymentEntityMutations.validatePayment({
      subscribeId,
      productId,
      bearerToken,
    }),
    onSuccess: () => {
      const query = new URLSearchParams(search.toString())
      if (productId) {
        query.set('productId', productId)
      } else {
        query.delete('productId')
      }
      router.push(`/subscribes/payment/${subscribeId}?${query.toString()}`)
    },
    onError: (error) => {
      if (error instanceof Error) {
        const cause = error.cause as BaseResponseSchema<null, 'DUPLICATE_LESSON' | 'DELINQUENT_PAYMENT'>

        openConfirmDialogV2({
          ConfirmHeader: () => <ConfirmHeader>{SUBSCRIBE_ERROR_MAP[cause.resultCdName]}</ConfirmHeader>,
          ConfirmFooter: ({ handleClose }) => {
            return (
              <Button size="xLarge" className="w-full" onClick={handleClose}>
                {t('common.actions.confirm')}
              </Button>
            )
          },
        })
      }
    },
  })

  const handleClickPurchaseButton = async () => {
    if (!isAuthorized) {
      const loginParams = new URLSearchParams(search.toString())

      if (loginDestination) {
        loginParams.set('destination', loginDestination)
      }

      return openAlertDialog({
        title: SUBSCRIBE_ERROR_MAP.NOT_AUTHORIZED,
        confirmMessage: t('common.auth.login'),
        handleConfirm: () => {
          const query = loginParams.toString()
          router.push(query ? `/login?${query}` : '/login')
        },
      })
    }

    const INVALID_PHONE_NUMBER = '000-0000-0000'

    if (!user?.phone || user.phone === '' || user.phone === INVALID_PHONE_NUMBER.replace(/-/g, '')) {
      return openAlertDialog({
        title: SUBSCRIBE_ERROR_MAP.MUST_REGISTER_PHONE_NUMBER,
        handleConfirm: () => {
          router.push('/my-podo')
        },
        confirmMessage: t('common.actions.confirm'),
      })
    }

    await validatePayment.mutateAsync()
  }
  return (
    <VStack className="p-5 rounded-xl border-gray-200 border gap-5" data-testid={`item-ticket-${subscribeId}`}>
      <VStack className="gap-2">
        <Flex className="justify-between">
          <Typography size="h3">{t('subscribeList.ticket.months', { count: month })}</Typography>
          {isBest && <BadgeV1 className="bg-red-light text-red-normal rounded-[14px] px-2">BEST</BadgeV1>}
        </Flex>
        <SubscribeOriginalPrice
          curriculumType={curriculumType}
          langType={langType}
          perMonth
          originalPrice={productId && curriculumType === 'SMART_TALK' ? catalogTicket?.originPrice : undefined}
        />
        <Flex className="items-center gap-0.5">
          <SubscribeDiscountBadge
            curriculumType={curriculumType}
            langType={langType}
            month={month}
            discountPercent={
              productId && curriculumType === 'SMART_TALK' ? (catalogTicket?.finalDiscountRate ?? undefined) : undefined
            }
          />
          <Typography size="h1" className="!text-[22px]">
            {formatKrwAmount(displayPrice, effectiveLocale)}
          </Typography>
          <Typography size="body1" className="text-gray-400">
            {t('subscribeList.ticket.perMonth')}
          </Typography>
        </Flex>
        <VStack>
          {descriptions.map((description, index) => (
            <Flex key={index} className="items-center gap-1.5">
              <LineCheckIcon />
              <Typography size="body1">{description}</Typography>
            </Flex>
          ))}
        </VStack>
      </VStack>
      <Button
        className="w-full"
        size="large"
        onClick={handleClickPurchaseButton}
        data-testid={`btn-purchase-${subscribeId}`}
        eventParams={{ name: UI_IDS.btn.purchase(subscribeId), location: 'subscribes', action: 'submit' }}
      >
        {t('subscribeList.ticket.purchase')}
      </Button>
    </VStack>
  )
}

const ConfirmHeader = ({ children }: { children?: ReactNode }) => {
  return (
    <VStack className="gap-2 items-center">
      <Typography size="h3" className="flex text-center flex-1 text-gray-900 min-h-12 items-center font-medium">
        {children}
      </Typography>
    </VStack>
  )
}

const LineCheckIcon = (props: ComponentProps<typeof Icon>) => (
  <Icon width={16} height={16} viewBox="0 0 16 16" {...props}>
    <path
      d="M4 8.5L6.47487 10.9749L12.1317 5.31802"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Icon>
)

export default SubscribeTicketCard
